// Deal Submission spec §10 — create/edit page for the
// DealSubmissionTemplate "group" (template + ordered children:
// sections + per-document upload requests). Editing an active
// template mints a new version (copy-on-edit); in-flight
// submissions stay pinned to their original version.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { Button, Card, CardContent, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import {
  dealSubmissionTemplatesApi,
  type CreateDealSubmissionTemplatePayload,
  type DealSubmissionSectionKind,
  type DealSubmissionTemplateDto,
  type TemplateDocumentRequestInput,
  type TemplateSectionInput,
  type UpdateDealSubmissionTemplatePayload,
} from "@/api/dealSubmissionTemplates";
import { dealSubmissionMappingTokensApi } from "@/api/dealSubmissionMappingTokens";
import {
  dealSubmissionDocumentPromptsApi,
  dealSubmissionPromptsApi,
} from "@/api/dealSubmissionTemplates";
import { documentTagsApi, type DocumentTagDto } from "@/api/reportsV32";
import { parseFormSchema, type FormSchema } from "@/lib/formSchema";
import { DealSubmissionFormBuilder } from "@/components/DealSubmission/DealSubmissionFormBuilder";
import { DealSubmissionPromptEditor } from "@/components/DealSubmission/DealSubmissionPromptEditor";
import {
  DealSubmissionDocumentRequestList,
  type DocumentRequestDraft,
} from "@/components/DealSubmission/DealSubmissionDocumentRequestList";

interface SectionDraft {
  /** Stable client-side id; survives reorder and is sent back on update
   *  so the service can match against the prior version's sections. */
  clientId: string;
  /** Server id (only present on existing rows). */
  serverId: string | null;
  title: string;
  kind: DealSubmissionSectionKind;
  instructions: string;
  required: boolean;
  visibleWhenJson: string;
  schema: FormSchema;
  documents: DocumentRequestDraft[];
  /** Per-section AI toggles — gate the broker-facing feedback and the
   *  staff-facing assessment distillation for this section respectively. */
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
}

const EMPTY_SCHEMA: FormSchema = { fields: [], version: "v1" };

function emptySection(): SectionDraft {
  return {
    clientId: crypto.randomUUID(),
    serverId: null,
    title: "New section",
    kind: "DataEntry",
    instructions: "",
    required: true,
    visibleWhenJson: "",
    schema: EMPTY_SCHEMA,
    documents: [],
    feedbackEnabled: true,
    assessmentEnabled: true,
  };
}

function fromDto(t: DealSubmissionTemplateDto): {
  name: string;
  description: string;
  brokerInstructions: string;
  sections: SectionDraft[];
} {
  const sections: SectionDraft[] = [...t.sections]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({
      clientId: crypto.randomUUID(),
      serverId: s.id,
      title: s.title,
      kind: s.kind,
      instructions: s.instructions ?? "",
      required: s.required,
      visibleWhenJson: s.visibleWhenJson ?? "",
      schema: parseFormSchema(s.formSchemaJson) ?? EMPTY_SCHEMA,
      documents: [],
      feedbackEnabled: s.feedbackEnabled,
      assessmentEnabled: s.assessmentEnabled,
    }));

  const sectionsById = new Map<string, SectionDraft>(
    sections.map((s) => [s.serverId ?? "", s]),
  );
  for (const d of [...t.documentRequests].sort((a, b) => a.order - b.order)) {
    const sec = sectionsById.get(d.sectionId);
    if (!sec) continue;
    sec.documents.push({
      id: d.id,
      serverId: d.id,
      key: d.key,
      label: d.label,
      required: d.required,
      allowedTypes: d.allowedTypes ?? "",
      documentTagCode: d.documentTagCode ?? "",
      destinationRule: d.destinationRule ?? "",
      instructions: d.instructions ?? "",
      feedbackEnabled: d.feedbackEnabled,
      assessmentEnabled: d.assessmentEnabled,
    });
  }

  return {
    name: t.name,
    description: t.description ?? "",
    brokerInstructions: t.brokerInstructions ?? "",
    sections: sections.length > 0 ? sections : [emptySection()],
  };
}

function toPayload(state: {
  name: string;
  description: string;
  brokerInstructions: string;
  sections: SectionDraft[];
}): CreateDealSubmissionTemplatePayload {
  const sections: TemplateSectionInput[] = state.sections.map((s, idx) => ({
    id: s.serverId,
    order: idx + 1,
    title: s.title.trim() || `Section ${idx + 1}`,
    kind: s.kind,
    instructions: s.instructions.trim() || null,
    required: s.required,
    visibleWhenJson: s.visibleWhenJson.trim() || null,
    formSchemaJson:
      s.kind === "DataEntry" ? JSON.stringify(s.schema) : null,
    feedbackEnabled: s.feedbackEnabled,
    assessmentEnabled: s.assessmentEnabled,
  }));

  const documentRequests: TemplateDocumentRequestInput[] = [];
  state.sections.forEach((s, sIdx) => {
    s.documents.forEach((d, dIdx) => {
      documentRequests.push({
        id: null,
        // Match by SectionOrder so a re-keyed section (new version)
        // still binds its document requests correctly.
        sectionId: null,
        sectionOrder: sIdx + 1,
        order: dIdx + 1,
        key: d.key.trim(),
        label: d.label.trim() || "Document",
        required: d.required,
        allowedTypes: d.allowedTypes.trim() || null,
        documentTagCode: d.documentTagCode.trim() || null,
        destinationRule: d.destinationRule.trim() || null,
        instructions: d.instructions.trim() || null,
        visibleWhenJson: null,
        feedbackEnabled: d.feedbackEnabled,
        assessmentEnabled: d.assessmentEnabled,
      });
    });
  });

  return {
    name: state.name.trim(),
    description: state.description.trim() || null,
    brokerInstructions: state.brokerInstructions.trim() || null,
    sections,
    documentRequests,
  };
}

export function DealSubmissionTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [brokerInstructions, setBrokerInstructions] = useState("");
  const [sections, setSections] = useState<SectionDraft[]>([emptySection()]);

  const existing = useQuery({
    queryKey: ["deal-submission-template", id],
    queryFn: () => dealSubmissionTemplatesApi.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing.data) {
      const next = fromDto(existing.data);
      setName(next.name);
      setDescription(next.description);
      setBrokerInstructions(next.brokerInstructions);
      setSections(next.sections);
    }
  }, [existing.data]);

  const tokens = useQuery({
    queryKey: ["deal-submission-mapping-tokens"],
    queryFn: () => dealSubmissionMappingTokensApi.list(),
  });

  const tags = useQuery({
    queryKey: ["document-tags"],
    queryFn: () => documentTagsApi.list(),
  });

  const create = useMutation({
    mutationFn: () =>
      dealSubmissionTemplatesApi.create(
        toPayload({ name, description, brokerInstructions, sections }),
      ),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Template '${dto.name}' created.` });
      void qc.invalidateQueries({
        queryKey: ["deal-submission-templates-admin"],
      });
      navigate(`/admin/deal-submission-templates/${dto.id}`);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const update = useMutation({
    mutationFn: () =>
      dealSubmissionTemplatesApi.update(
        id!,
        toPayload({
          name,
          description,
          brokerInstructions,
          sections,
        }) as UpdateDealSubmissionTemplatePayload,
      ),
    onSuccess: (dto) => {
      toast({
        tone: "success",
        message: `Saved as v${dto.version}. In-flight submissions keep their pinned version.`,
      });
      void qc.invalidateQueries({
        queryKey: ["deal-submission-templates-admin"],
      });
      // Editing creates a new version with a NEW id — navigate there.
      navigate(`/admin/deal-submission-templates/${dto.id}`);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const validation = useMemo(
    () => validate({ name, sections }),
    [name, sections],
  );
  const canSave =
    validation.length === 0 && !create.isPending && !update.isPending;

  function patchSection(clientId: string, patch: Partial<SectionDraft>) {
    setSections((prev) =>
      prev.map((s) => (s.clientId === clientId ? { ...s, ...patch } : s)),
    );
  }

  function moveSection(clientId: string, dir: -1 | 1) {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.clientId === clientId);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  function removeSection(clientId: string) {
    setSections((prev) => prev.filter((s) => s.clientId !== clientId));
  }

  function addSection() {
    setSections((prev) => [...prev, emptySection()]);
  }

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Deal Submission · Template"
        title={
          isNew
            ? "New deal submission template"
            : existing.data?.name ?? "Edit template"
        }
        description={
          isNew
            ? "Author the wizard the broker sees. Add sections (DataEntry or Documents) and per-document upload requests."
            : `Editing creates a new version (v${
                (existing.data?.version ?? 0) + 1
              }). In-flight submissions stay on v${
                existing.data?.version ?? "?"
              }.`
        }
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/admin/deal-submission-templates")}
            >
              <ArrowLeft size={14} className="mr-space-1" /> Back
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              onClick={() => (isNew ? create.mutate() : update.mutate())}
            >
              <Save size={14} className="mr-space-1" />
              {isNew ? "Create template" : "Save (new version)"}
            </Button>
          </div>
        }
      />

      {validation.length > 0 && (
        <div className="rounded-md bg-status-error/10 p-space-3 text-small text-onyx">
          <ul className="m-0 pl-space-4">
            {validation.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        </div>
      )}

      <Card>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-space-3 p-space-4">
          <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
            <span>
              Name <span className="text-error">*</span>
            </span>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
            <span>Description</span>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
            <span>Broker-facing instructions (shown above the wizard)</span>
            <Textarea
              rows={3}
              value={brokerInstructions}
              onChange={(e) => setBrokerInstructions(e.target.value)}
            />
          </label>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-h5 font-display text-onyx m-0">
          Sections ({sections.length})
        </h2>
        <Button variant="secondary" size="sm" type="button" onClick={addSection}>
          <Plus size={14} className="mr-space-1" /> Add section
        </Button>
      </div>

      <ol className="flex flex-col gap-space-3 list-none p-0">
        {sections.map((section, idx) => (
          <li key={section.clientId}>
            <SectionEditor
              section={section}
              index={idx}
              total={sections.length}
              templateId={id ?? null}
              mappingTokens={tokens.data ?? []}
              documentTags={tags.data ?? []}
              onChange={(patch) => patchSection(section.clientId, patch)}
              onMoveUp={() => moveSection(section.clientId, -1)}
              onMoveDown={() => moveSection(section.clientId, 1)}
              onRemove={() => removeSection(section.clientId)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

interface SectionEditorProps {
  section: SectionDraft;
  index: number;
  total: number;
  /** Saved template id; null on the unsaved/new-template flow. */
  templateId: string | null;
  mappingTokens: ReadonlyArray<{
    token: string;
    label: string;
    namespace: string;
    target: string;
    valueKind: string;
    description: string | null;
  }>;
  documentTags: ReadonlyArray<DocumentTagDto>;
  onChange: (patch: Partial<SectionDraft>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function SectionEditor({
  section,
  index,
  total,
  templateId,
  mappingTokens,
  documentTags,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: SectionEditorProps) {
  // Per-section AI prompts are keyed on the persisted section id, so they
  // are only authorable once the section exists server-side. Fetch the
  // saved pair lazily; the editor stays hidden on unsaved/new sections.
  const prompts = useQuery({
    queryKey: ["deal-submission-section-prompts", templateId, section.serverId],
    queryFn: () => dealSubmissionPromptsApi.list(templateId!, section.serverId!),
    enabled: Boolean(templateId && section.serverId),
  });

  return (
    <Card>
      <CardContent className="flex flex-col gap-space-3 p-space-4">
        <div className="flex items-start justify-between gap-space-2">
          <div className="flex flex-col gap-space-1 flex-1">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              Section {index + 1} · {section.kind}
            </span>
            <Input
              aria-label={`Section ${index + 1} title`}
              value={section.title}
              onChange={(e) => onChange({ title: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-space-1">
            <Button
              variant="secondary"
              size="sm"
              type="button"
              aria-label="Move section up"
              onClick={onMoveUp}
              disabled={index === 0}
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              aria-label="Move section down"
              onClick={onMoveDown}
              disabled={index === total - 1}
            >
              <ArrowDown size={14} />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              type="button"
              aria-label="Remove section"
              onClick={onRemove}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Kind</span>
            <Select
              value={section.kind}
              onChange={(e) =>
                onChange({
                  kind: e.target.value as DealSubmissionSectionKind,
                })
              }
            >
              <option value="DataEntry">DataEntry — structured fields</option>
              <option value="Documents">Documents — uploads only</option>
            </Select>
            <span className="text-caption text-gray-mid">
              DataEntry collects answers; Documents holds upload requests.
              Either kind can carry document-request items below.
            </span>
          </label>
          <label className="flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={section.required}
              onChange={(e) => onChange({ required: e.target.checked })}
            />
            Section required for submission
          </label>
        </div>

        <label className="flex flex-col gap-space-1 text-small text-onyx">
          <span>Instructions (shown at top of section)</span>
          <Textarea
            rows={2}
            value={section.instructions}
            onChange={(e) => onChange({ instructions: e.target.value })}
          />
        </label>

        <label className="flex flex-col gap-space-1 text-small text-onyx">
          <span>Visible-when (JSON, optional)</span>
          <Input
            value={section.visibleWhenJson}
            onChange={(e) => onChange({ visibleWhenJson: e.target.value })}
            placeholder='e.g. {"field":"deal_type","equals":"asset"}'
          />
          <span className="text-caption text-gray-mid">
            Same shape as a form-field visibleWhen — controls whether the
            whole section shows in the wizard.
          </span>
        </label>

        {section.kind === "DataEntry" && (
          <div className="border-t border-border-light pt-space-3">
            <h3 className="text-h6 font-display text-onyx m-0 mb-space-2">
              Data fields
            </h3>
            <DealSubmissionFormBuilder
              schema={section.schema}
              onChange={(schema) => onChange({ schema })}
              mappingTokens={mappingTokens}
            />
          </div>
        )}

        <div className="border-t border-border-light pt-space-3">
          <h3 className="text-h6 font-display text-onyx m-0 mb-space-2">
            Document requests ({section.documents.length})
          </h3>
          <p className="text-caption text-gray-mid m-0 mb-space-2">
            Each row becomes its own upload control at runtime — one blob,
            one virus scan, one filing destination per document.
          </p>
          <DealSubmissionDocumentRequestList
            items={section.documents}
            onChange={(documents) => onChange({ documents })}
            documentTags={documentTags}
          />

          {templateId &&
            section.documents
              .filter((d) => d.serverId)
              .map((doc) => (
                <DocumentPromptsBlock
                  key={doc.id}
                  templateId={templateId}
                  document={doc}
                  onChange={(patch) =>
                    onChange({
                      documents: section.documents.map((d) =>
                        d.id === doc.id ? { ...d, ...patch } : d,
                      ),
                    })
                  }
                />
              ))}
        </div>

        <div className="border-t border-border-light pt-space-3">
          <h3 className="text-h6 font-display text-onyx m-0 mb-space-2">
            AI prompts
          </h3>
          <div className="flex flex-col gap-space-2 mb-space-3">
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                className="mt-space-1"
                checked={section.feedbackEnabled}
                onChange={(e) => onChange({ feedbackEnabled: e.target.checked })}
                data-testid="section-ai-toggle-feedback"
              />
              <span className="flex flex-col">
                <span>Show AI feedback to the broker</span>
                <span className="text-caption text-gray-mid">
                  When off, this section's feedback prompt is ignored on section
                  completion even if a body is authored.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                className="mt-space-1"
                checked={section.assessmentEnabled}
                onChange={(e) => onChange({ assessmentEnabled: e.target.checked })}
                data-testid="section-ai-toggle-assessment"
              />
              <span className="flex flex-col">
                <span>Generate an AI assessment for staff</span>
                <span className="text-caption text-gray-mid">
                  When off, this section's assessment is skipped and the staging
                  review won't show a distillation for it.
                </span>
              </span>
            </label>
          </div>
          {templateId && section.serverId ? (
            <DealSubmissionPromptEditor
              templateId={templateId}
              sectionId={section.serverId}
              initialPrompts={prompts.data ?? []}
            />
          ) : (
            <p className="text-caption text-gray-mid m-0">
              Save the template first to author this section's AI feedback and
              assessment prompts.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface DocumentPromptsBlockProps {
  templateId: string;
  document: DocumentRequestDraft;
  onChange: (patch: Partial<DocumentRequestDraft>) => void;
}

// AI evaluation — per-document AI prompts block. Mirrors the per-section
// block: two toggles + a Feedback / Assessment prompt pair, scoped to a saved
// document-request id (the parent only renders this for documents that have a
// server id, since prompts key on the persisted id).
function DocumentPromptsBlock({
  templateId,
  document,
  onChange,
}: DocumentPromptsBlockProps) {
  const prompts = useQuery({
    queryKey: ["deal-submission-document-prompts", templateId, document.serverId],
    queryFn: () =>
      dealSubmissionDocumentPromptsApi.list(templateId, document.serverId!),
    enabled: Boolean(document.serverId),
  });

  return (
    <div className="border-t border-border-light pt-space-3 mt-space-3">
      <h4 className="text-h6 font-display text-onyx m-0 mb-space-2">
        AI prompts · {document.label}
      </h4>
      <div className="flex flex-col gap-space-2 mb-space-3">
        <label className="flex items-start gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            className="mt-space-1"
            checked={document.feedbackEnabled}
            onChange={(e) => onChange({ feedbackEnabled: e.target.checked })}
            data-testid="document-ai-toggle-feedback"
          />
          <span className="flex flex-col">
            <span>Show AI feedback to the recipient</span>
            <span className="text-caption text-gray-mid">
              When off, this document's feedback prompt is ignored when the
              broker runs Evaluate & Feedback.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            className="mt-space-1"
            checked={document.assessmentEnabled}
            onChange={(e) => onChange({ assessmentEnabled: e.target.checked })}
            data-testid="document-ai-toggle-assessment"
          />
          <span className="flex flex-col">
            <span>Generate an AI assessment for staff</span>
            <span className="text-caption text-gray-mid">
              When off, this document's assessment is skipped and the staging
              review won't show a distillation for it.
            </span>
          </span>
        </label>
      </div>
      <DealSubmissionPromptEditor
        templateId={templateId}
        documentRequestId={document.serverId!}
        initialPrompts={prompts.data ?? []}
      />
    </div>
  );
}

function validate(state: {
  name: string;
  sections: SectionDraft[];
}): string[] {
  const errors: string[] = [];
  if (!state.name.trim()) errors.push("Name is required.");
  if (state.sections.length === 0) errors.push("At least one section is required.");
  state.sections.forEach((s, i) => {
    if (!s.title.trim()) errors.push(`Section ${i + 1}: title is required.`);
    if (s.kind === "DataEntry" && s.schema.fields.length === 0) {
      errors.push(
        `Section ${i + 1} (${s.title || "untitled"}): DataEntry sections need at least one field.`,
      );
    }
    s.documents.forEach((d, di) => {
      if (!d.key.trim()) {
        errors.push(
          `Section ${i + 1} document ${di + 1}: key is required.`,
        );
      }
      if (!d.label.trim()) {
        errors.push(
          `Section ${i + 1} document ${di + 1}: label is required.`,
        );
      }
    });
    // Unique key check within section
    const seen = new Set<string>();
    for (const d of s.documents) {
      if (d.key && seen.has(d.key)) {
        errors.push(
          `Section ${i + 1}: duplicate document key '${d.key}'.`,
        );
        break;
      }
      if (d.key) seen.add(d.key);
    }
  });
  // Global unique-key check across the template version.
  const allKeys = new Set<string>();
  for (const s of state.sections) {
    for (const d of s.documents) {
      if (!d.key) continue;
      if (allKeys.has(d.key)) {
        errors.push(`Document key '${d.key}' is used in multiple sections.`);
        break;
      }
      allKeys.add(d.key);
    }
  }
  return errors;
}
